<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h1>Chatbot Conversations</h1>
    </div>

    <div class="card">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="d-flex align-items-center">
            <div class="btn-group mr-3" role="group">
              <button
                v-for="statusOption in statusOptions"
                :key="statusOption.value"
                type="button"
                class="btn btn-sm"
                :class="selectedStatus === statusOption.value ? 'btn-primary' : 'btn-outline-primary'"
                @click="filterByStatus(statusOption.value)"
              >
                {{ statusOption.label }}
              </button>
            </div>
          </div>
        </div>

        <LoadingIndicator centered v-if="loading" />

        <table class="table table-hover" v-if="!loading && conversations && conversations.length > 0">
          <thead>
            <tr>
              <th scope="col">ID</th>
              <th scope="col">Status</th>
              <th scope="col">Contact</th>
              <th scope="col">Intent Score</th>
              <th scope="col">Vehicle</th>
              <th scope="col">Route</th>
              <th scope="col">Last Message</th>
              <th scope="col">Messages</th>
              <th scope="col">Last Activity</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="conversation in conversations"
              :key="conversation.id"
              class="clickable-row"
              :class="{ 'high-intent': conversation.context.intentScore > 70 }"
              @click="viewConversation(conversation.id)"
            >
              <td>#{{ conversation.id }}</td>
              <td>
                <ConversationStatusBadge :status="conversation.status" />
              </td>
              <td>
                <div v-if="conversation.context.contactName">
                  <strong>{{ conversation.context.contactName }}</strong>
                </div>
                <div v-if="conversation.context.contactEmail" class="text-muted small">
                  {{ conversation.context.contactEmail }}
                </div>
                <div v-if="conversation.context.contactPhone" class="text-muted small">
                  {{ conversation.context.contactPhone | formatPhone}}
                </div>
                <div v-if="!conversation.context.contactName && !conversation.context.contactEmail && !conversation.context.contactPhone" class="text-muted">
                  No contact info
                </div>
              </td>
              <td>
                <IntentScoreBadge
                  v-if="conversation.context.intentScore !== null && conversation.context.intentScore !== undefined"
                  :score="conversation.context.intentScore"
                />
                <span v-else class="text-muted">-</span>
              </td>
              <td>
                <span v-if="conversation.context.vehicle">{{ conversation.context.vehicle }}</span>
                <span v-else class="text-muted">-</span>
              </td>
              <td>
                <span v-if="conversation.context.route" class="small">{{ conversation.context.route }}</span>
                <span v-else class="text-muted">-</span>
              </td>
              <td>
                <div v-if="conversation.lastMessage" class="last-message">
                  <div class="message-content">{{ truncateMessage(conversation.lastMessage.content) }}</div>
                  <div class="text-muted small">{{ conversation.lastMessage.sender_type }}</div>
                </div>
                <span v-else class="text-muted">No messages</span>
              </td>
              <td>
                <span class="badge badge-light">{{ conversation.messageCount }}</span>
              </td>
              <td>
                <span :title="formatDateTime(conversation.lastActivityAt)">
                  {{ formatTimeAgo(conversation.lastActivityAt) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <EmptyState v-if="!loading && (!conversations || conversations.length === 0)" message="No conversations found" />

        <PageLinks
          :current-page="currentPage"
          :has-next-page="hasNextPage"
          :on-page-change="updatePageUrlParam"
          :is-disabled="loading"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ApiClient } from '@/api/ApiClient';
import pageLinksMixin from '@/mixins/pageLinks';
import LoadingIndicator from '@/components/shared/LoadingIndicator';
import EmptyState from '@/components/shared/EmptyState';
import ConversationStatusBadge from '@/components/shared/ConversationStatusBadge';
import IntentScoreBadge from '@/components/shared/IntentScoreBadge';
import moment from 'moment';

export default {
  name: 'ConversationList',
  mixins: [pageLinksMixin],
  components: {
    LoadingIndicator,
    EmptyState,
    ConversationStatusBadge,
    IntentScoreBadge,
  },
  data() {
    return {
      conversations: [],
      loading: false,
      selectedStatus: 'active',
      statusOptions: [
        { label: 'All', value: 'all' },
        { label: 'Active', value: 'active' },
        { label: 'Taken Over', value: 'taken_over' },
        { label: 'Closed', value: 'closed' },
        { label: 'Converted', value: 'converted' },
      ],
    };
  },
  computed: {
    hasNextPage() {
      return this.conversations.length === this.perPage;
    },
    fetchParams() {
      const params = {};
      const { status } = this.$route.query;
      if (status) {
        params.status = status;
      } else {
        params.status = this.selectedStatus;
      }
      params.page = this.currentPage;
      params.limit = this.perPage;
      return params;
    },
  },
  methods: {
    updateRouteQuery(newQuery) {
      const oldQuery = this.$route.query;
      const query = Object.assign({}, oldQuery, newQuery);
      this.$router.push({ query });
    },
    async fetch() {
      if (this.loading) return;
      this.loading = true;
      const response = await ApiClient.chatbot.conversations.get_all({ params: this.fetchParams });
      this.loading = false;
      if (response.status === 200) {
        this.conversations = response.data.data || [];
      }
      else {
        this.conversations = []
      }
    },
    filterByStatus(status) {
      this.selectedStatus = status;
      this.updateRouteQuery({ status, page: 1 });
    },
    viewConversation(id) {
      this.$router.push({ name: 'ChatbotConversationDetail', params: { id } });
    },
    formatDateTime(dateTime) {
      if (!dateTime) return '';
      return moment(dateTime).format('MMM D, YYYY h:mm A');
    },
    formatTimeAgo(dateTime) {
      if (!dateTime) return '';
      return moment(dateTime).fromNow();
    },
    truncateMessage(message, maxLength = 60) {
      if (!message) return '';
      if (message.length <= maxLength) return message;
      return message.substring(0, maxLength) + '...';
    },
    handleWebSocketMessage(event) {
      const { message, conversation } = event;

      // Find the conversation in the current list
      const conversationIndex = this.conversations.findIndex(c => c.id === conversation.id);

      if (conversationIndex !== -1) {
        // Update existing conversation
        const existingConversation = this.conversations[conversationIndex];

        // Update conversation properties
        existingConversation.lastMessage = message;
        existingConversation.lastActivityAt = message.created_at;
        existingConversation.messageCount = (existingConversation.messageCount || 0) + 1;
        existingConversation.status = conversation.status;

        // Move conversation to the top if it's not already there
        if (conversationIndex !== 0) {
          this.conversations.splice(conversationIndex, 1);
          this.conversations.unshift(existingConversation);
        }
      } else {
        // New conversation that matches current filter - fetch fresh data
        this.fetch();
      }
    },
  },
  watch: {
    fetchParams() {
      this.fetch();
    },
  },
  created() {
    // Set initial status from query param if exists
    if (this.$route.query.status) {
      this.selectedStatus = this.$route.query.status;
    }
    this.fetch();

    // Listen to WebSocket channel for real-time updates
    this.$echo.private('chatbot.conversations').listen('.ChatbotMessageReceived', e => {
      this.handleWebSocketMessage(e);
    });
  },
  beforeDestroy() {
    // Clean up WebSocket connection
    this.$echo.leave('chatbot.conversations');
  },
};
</script>

<style lang="scss" scoped>
.clickable-row {
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }

  &.high-intent {
    border-left: 3px solid #28a745;
  }
}

.last-message {
  .message-content {
    max-width: 250px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.btn-group {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
</style>
